import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {loginUser} from '@store/reducers/auth';
import {setWindowClass} from '@app/utils/helpers';
import {PfButton, PfCheckbox} from '@profabric/react-components';
import ReCAPTCHA from 'react-google-recaptcha';

import * as Yup from 'yup';

import {Form, InputGroup} from 'react-bootstrap';
import * as AuthService from '../../services/auth';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthLoading, setAuthLoading] = useState(false);
  const [isGoogleAuthLoading, setGoogleAuthLoading] = useState(false);
  const [isFacebookAuthLoading, setFacebookAuthLoading] = useState(false);
  const [validCaptcha, setValidCaptcha] = useState(false);

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const [t] = useTranslation();

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const {handleChange, values, handleSubmit, touched, errors} = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
      password: Yup.string()
        .min(5, 'Must be 5 characters or more')
        .max(30, 'Must be 30 characters or less')
        .required('Required')
    }),
    onSubmit: (values) => {
      login(values.email, values.password);
    }
  });

  setWindowClass('hold-transition login-page');

  const login = async (email: string, password: string) => {
    if (!validCaptcha) {
      toast.error('Please verify captcha');
      return;
    }

    setAuthLoading(true);
    const resp = await AuthService.loginByAuth(email, password);
    if (!resp) {
      setAuthLoading(false);
      toast.error('Something went wrong.');
      return;
    }
    if (resp.status === 200) {
      const token = localStorage.getItem('token');
      toast.success('Login is succeed!');
      setAuthLoading(false);
      dispatch(loginUser(token));
      navigate('/');
    }

    if (resp.status !== 200) {
      setAuthLoading(false);
      errors.email = 'Invalid email address!';
      errors.password = 'Invalid password!';
      toast.error('Incorrect email and password!');
    }
  };

  return (
    <div className="login-box">
      <div className="card card-outline card-primary">
        <div className="card-header text-center">
          <Link to="/" className="h1">
            <b>Artzen</b>
            <span>Wise</span>
          </Link>
        </div>
        <div className="card-body">
          <p className="login-box-msg">{t<string>('login.label.signIn')}</p>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <InputGroup hasValidation>
                <InputGroup.Text id="basic-addon1">
                  <i className="fas fa-envelope" />
                </InputGroup.Text>
                <Form.Control
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  aria-describedby="basic-addon1"
                  onChange={handleChange}
                  value={values.email}
                  isValid={touched.email && !errors.email}
                  isInvalid={touched.email && !!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </InputGroup>
            </div>

            <div className="mb-3">
              <InputGroup className="mb-3">
                <InputGroup.Text
                  onClick={togglePasswordVisibility}
                  style={{cursor: 'pointer', width: '42px'}}
                >
                  <i
                    className={
                      showPassword ? 'fas fa-lock-open' : 'fas fa-lock'
                    }
                  />
                </InputGroup.Text>
                <Form.Control
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  onChange={handleChange}
                  value={values.password}
                  isValid={touched.password && !errors.password}
                  isInvalid={touched.password && !!errors.password}
                />
                {/* <InputGroup.Text
                  onClick={togglePasswordVisibility}
                  style={{cursor: 'pointer'}}
                >
                  <i
                    className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}
                  />
                </InputGroup.Text> */}
                {touched.password && errors.password && (
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                )}
              </InputGroup>
            </div>
            <div className="mb-3">
              <ReCAPTCHA
                sitekey="6LcdXLslAAAAAIP39X6-xttAPW54YqLPFWsvWMG0"
                onChange={(e) => {
                  setValidCaptcha(true);
                }}
              />
            </div>
            <div className="row">
              <div className="col-8">
                <PfCheckbox checked={false}>
                  {t<string>('login.label.rememberMe')}
                </PfCheckbox>
              </div>
              <div className="col-4">
                <PfButton
                  block
                  type="submit"
                  loading={isAuthLoading}
                  disabled={isFacebookAuthLoading || isGoogleAuthLoading}
                >
                  {t<string>('login.button.signIn.label')}
                </PfButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
